import React, { Fragment, useEffect, useRef, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

import { GetInvestigationCategories } from "../../../../api/diagnosis/master/investigationCategoryAPI";
import { StorePanel } from "../../../../api/diagnosis/master/panelAPI";
import { GetDepartmentalSubSpecialities } from "../../../../api/diagnosis/master/departmentalSubSpecilityAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// =========================
// Validation Schema
// =========================
const schema = yup.object().shape({
  name: yup.string().required("Panel name is required"),

  departmentalSubSpecialityId: yup.string().required("Departmental Sub Speciality is required"),
  rate: yup.number().typeError("Rate must be a number").required("Rate is required"),
  nightCharges: yup.number().typeError("Night charges must be a number").nullable(),
  discountAllow: yup.string().oneOf(["Yes", "No"]).required("Discount Allow is required"),
  active: yup.string().oneOf(["Yes", "No"]).required("Status is required"),

  // ✅ Test Details Array
  testDetails: yup
    .array()
    .of(
      yup.object().shape({
        printSrNo: yup.number().typeError("Print Sr No must be a number").required("Print Sr No is required"),
        testName: yup.string().required("Test Name is required"),
        departmentalSubSpecialityId: yup.string().required("Departmental Sub Speciality is required")
      })
    )
    .min(1, "At least one test is required")
});


const AddPanel = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [departmentalSubSpecialities, setDepartmentalSubSpecialities] = useState([]);

  const { register, control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      testDetails: [
        { printSrNo: "", testName: "", departmentalSubSpecialityId: "" }
      ]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "testDetails"
  });

  const fetchSubSpeciality = async () => {
    try {
      const res = await GetDepartmentalSubSpecialities();
      if (res.success) setDepartmentalSubSpecialities(res.data);
      else showToast("error", res.message);
    } catch (err) {
      showToast("error", err.message);
    }
  };

  useEffect(() => {
    fetchSubSpeciality();
  }, []);
  // =========================
  // Submit Handler
  // =========================
  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const response = await StorePanel(data);
      if (response.success) {
        showToast("success", response.message);
        navigate(`/diagnosis/master/panel/list`);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", "Failed to create panel");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
    useKeyboardSubmit(handleSubmit, onSubmit);
    useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Panel Master" title="Add Panel" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Add Panel</h5>
            <Link to={`/diagnosis/master/panel/list`}>
              <button className="btn btn-primary" id="list">
                List Panels
              </button>
              <UncontrolledTooltip placement="top" target="list">
                List Panels
              </UncontrolledTooltip>
            </Link>
          </div>

          <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
            <div className="card-body">
              <div className="row">
                {/* Panel Name */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Panel Name</label>
                    <input
                      className="form-control"
                      placeholder="Enter panel name"
                      {...register("name")}
                    />
                    {errors.name &&
                      <p className="text-danger">
                        {errors.name.message}
                      </p>}
                  </div>
                </div>

                <div className="col-md-6">
                  <label>Sub Speciality *</label>
                  <select
                    className="form-control"
                    {...register("departmentalSubSpecialityId")}
                  >
                    <option value="">Select Sub Speciality</option>
                    {departmentalSubSpecialities.map(item => (
                      <option key={item._id} value={item._id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  {errors.departmentalSubSpecialityId &&
                    <p className="text-danger">
                      {errors.departmentalSubSpecialityId.message}
                    </p>}
                </div>

                {/* Rate */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Rate</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter rate"
                      {...register("rate")}
                    />
                    {errors.rate &&
                      <p className="text-danger">
                        {errors.rate.message}
                      </p>}
                  </div>
                </div>

                {/* Night Charges */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Night Charges</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter night charges"
                      {...register("nightCharges")}
                    />
                    {errors.nightCharges &&
                      <p className="text-danger">
                        {errors.nightCharges.message}
                      </p>}
                  </div>
                </div>

                {/* Discount Allow */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Discount Allowed</label>
                    <select
                      className="form-control"
                      {...register("discountAllow")}
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                    {errors.discountAllow &&
                      <p className="text-danger">
                        {errors.discountAllow.message}
                      </p>}
                  </div>
                </div>

                {/* Active */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Status</label>
                    <select className="form-control" {...register("active")}>
                      <option value="Yes">Active</option>
                      <option value="No">Inactive</option>
                    </select>
                    {errors.active &&
                      <p className="text-danger">
                        {errors.active.message}
                      </p>}
                  </div>
                </div>
              </div>
              <hr />
              <h5>Test Details</h5>

              {fields.map((item, index) => (
                <div className="row align-items-end" key={item.id}>
                  {/* Print Sr No */}
                  <div className="col-md-2">
                    <label>Print Sr No</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`testDetails.${index}.printSrNo`)}
                    />
                    {errors.testDetails?.[index]?.printSrNo && (
                      <p className="text-danger">
                        {errors.testDetails[index].printSrNo.message}
                      </p>
                    )}
                  </div>

                  {/* Test Name */}
                  <div className="col-md-4">
                    <label>Test Name</label>
                    <input
                      className="form-control"
                      {...register(`testDetails.${index}.testName`)}
                    />
                    {errors.testDetails?.[index]?.testName && (
                      <p className="text-danger">
                        {errors.testDetails[index].testName.message}
                      </p>
                    )}
                  </div>

                  {/* Departmental Sub Speciality */}
                  <div className="col-md-4">
                    <label>Sub Speciality</label>
                    <select
                      className="form-control"
                      {...register(
                        `testDetails.${index}.departmentalSubSpecialityId`
                      )}
                    >
                      <option value="">Select</option>
                      {departmentalSubSpecialities.map(item => (
                        <option key={item._id} value={item._id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    {errors.testDetails?.[index]?.departmentalSubSpecialityId && (
                      <p className="text-danger">
                        {errors.testDetails[index].departmentalSubSpecialityId.message}
                      </p>
                    )}
                  </div>

                  {/* Remove Button */}
                  <div className="col-md-2">
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => remove(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="btn btn-success mt-2"
                onClick={() =>
                  append({
                    printSrNo: "",
                    testName: "",
                    departmentalSubSpecialityId: ""
                  })
                }
              >
                + Add Test
              </button>
            </div>

            <div className="card-footer text-end">
              <button className="btn btn-primary" type="submit">
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default AddPanel;
